/**
 * GridCrash — Replay Recording (storage pipeline only; playback UI is
 * a future phase — the dev validator below proves the format).
 *
 * Records the player's input stream ({t, a, x, y} events, t = ms since
 * first reveal, a = "r" reveal | "f" flag, x = col, y = row) and, on a
 * WIN that beats the current 100th-place score for its difficulty,
 * submits it to the `replays` table. A DB trigger prunes below rank 100.
 *
 * Sanitization keeps payloads small and honest:
 * - duplicate (a,x,y) within 150ms are dropped (double-fires)
 * - out-of-bounds coords are dropped
 * - hard cap 2,500 events (legit expert wins are well under 600)
 */

import { getClient, ensureSession } from "./auth.js";
import type { RewardTier } from "./profile.js";

export interface ReplayEvent {
  t: number;
  a: "r" | "f";
  x: number;
  y: number;
}

const DUPLICATE_WINDOW_MS = 150;
const MAX_EVENTS = 2500;

class ReplayRecorder {
  private events: ReplayEvent[] = [];
  private t0: number | null = null;
  private rows = 0;
  private cols = 0;

  /** Arms the recorder for a new game. Call on every game start. */
  reset(rows: number, cols: number): void {
    this.events = [];
    this.t0 = null;
    this.rows = rows;
    this.cols = cols;
  }

  /** Timing starts at the first recorded event (the opening reveal). */
  record(a: "r" | "f", row: number, col: number): void {
    if (this.rows === 0 || this.events.length >= MAX_EVENTS) return;
    if (row < 0 || row >= this.rows || col < 0 || col >= this.cols) return;

    if (this.t0 === null) this.t0 = performance.now();
    const t = Math.round(performance.now() - this.t0);

    const prev = this.events[this.events.length - 1];
    if (prev && prev.a === a && prev.x === col && prev.y === row &&
        t - prev.t < DUPLICATE_WINDOW_MS) {
      return;
    }

    this.events.push({ t, a, x: col, y: row });
  }

  /** Frozen copy of the stream; empty if nothing was recorded. */
  finish(): ReplayEvent[] {
    return [...this.events];
  }
}

export const replayRecorder = new ReplayRecorder();

// ─── Submission ───────────────────────────────────────────────────────────────

export interface ReplaySubmission {
  difficulty: RewardTier;
  finalScore: number;
  durationMs: number;
  seed: string | null;
  events: ReplayEvent[];
  /** Reconstruction metadata (5E playback): board generation inputs. */
  mode: "random" | "deterministic";
  engineDifficulty: string; // easy | medium | hard
  playerName: string | null;
}

/**
 * Submits a winning run if it would land in the top 100 for its
 * difficulty. Fire-and-forget safe: every failure path returns false.
 */
export async function submitReplayIfTop100(sub: ReplaySubmission): Promise<boolean> {
  if (sub.events.length === 0) return false;

  const userId = await ensureSession();
  const client = await getClient();
  if (!userId || !client) return false;

  try {
    // Skip the insert (and the prune churn) when we can't rank anyway.
    // Daily replays rank per seed (top 20 per day's board — the ghost
    // pool for Ghost Racing); other difficulties rank bucket-wide.
    const isDaily = sub.difficulty === "daily";
    let cutoffQuery = client
      .from("replays")
      .select("final_score")
      .eq("difficulty", sub.difficulty);
    if (isDaily && sub.seed !== null) cutoffQuery = cutoffQuery.eq("seed", sub.seed);
    const { data: cutoff } = await cutoffQuery
      .order("final_score", { ascending: false })
      .range(isDaily ? 19 : 99, isDaily ? 19 : 99);
    const cutoffRow = (cutoff as Array<{ final_score: number }> | null)?.[0];
    if (cutoffRow && cutoffRow.final_score >= sub.finalScore) return false;

    const { error } = await client.from("replays").insert({
      profile_id: userId,
      difficulty: sub.difficulty,
      final_score: sub.finalScore,
      duration_ms: sub.durationMs,
      seed: sub.seed,
      input_stream: sub.events,
      mode: sub.mode,
      engine_difficulty: sub.engineDifficulty,
      player_name: sub.playerName,
    });
    return !error;
  } catch {
    return false;
  }
}

// ─── Playback fetching (5E) ───────────────────────────────────────────────────

export interface ReplayMeta {
  id: string;
  player_name: string | null;
  final_score: number;
  duration_ms: number;
  seed: string | null;
  mode: "random" | "deterministic" | null;
  engine_difficulty: string | null;
  created_at: string;
}

/** A replay is reconstructable when its board-generation inputs survived. */
export function isWatchable(meta: ReplayMeta): boolean {
  return meta.seed !== null && meta.mode !== null && meta.engine_difficulty !== null;
}

/** Top runs for a difficulty — metadata only; streams load lazily on Watch. */
export async function fetchTopReplays(difficulty: RewardTier, limit = 10): Promise<ReplayMeta[]> {
  const client = await getClient();
  if (!client) return [];
  try {
    const { data } = await client
      .from("replays")
      .select("id, player_name, final_score, duration_ms, seed, mode, engine_difficulty, created_at")
      .eq("difficulty", difficulty)
      .order("final_score", { ascending: false })
      .limit(limit);
    return (data as ReplayMeta[] | null) ?? [];
  } catch {
    return [];
  }
}

/** Best replay recorded for a specific daily seed — the ghost to race. */
export async function fetchDailyGhost(seed: string): Promise<ReplayMeta | null> {
  const client = await getClient();
  if (!client) return null;
  try {
    const { data } = await client
      .from("replays")
      .select("id, player_name, final_score, duration_ms, seed, mode, engine_difficulty, created_at")
      .eq("difficulty", "daily")
      .eq("seed", seed)
      .order("final_score", { ascending: false })
      .limit(1);
    const meta = (data as ReplayMeta[] | null)?.[0];
    return meta && isWatchable(meta) ? meta : null;
  } catch {
    return null;
  }
}

export async function fetchReplayStream(id: string): Promise<ReplayEvent[] | null> {
  const client = await getClient();
  if (!client) return null;
  try {
    const { data } = await client
      .from("replays")
      .select("input_stream")
      .eq("id", id)
      .maybeSingle();
    const stream = (data as { input_stream: ReplayEvent[] } | null)?.input_stream;
    return Array.isArray(stream) ? stream : null;
  } catch {
    return null;
  }
}

// ─── Dev-only stream validator ────────────────────────────────────────────────

if (import.meta.env.DEV) {
  // Re-simulates an input stream against a fresh (seeded) engine and
  // reports the outcome — proves recordings are replayable before any
  // playback UI exists. Usage from the console:
  //   __gridclashReplayCheck(events, "easy", "random", seed?)
  interface ReplayCheckResult {
    phase: string;
    score: number;
    eventsApplied: number;
  }

  (window as unknown as Record<string, unknown>)["__gridclashReplayCheck"] =
    async (
      events: ReplayEvent[],
      difficultyId: string,
      mode: "random" | "deterministic",
      seed?: number
    ): Promise<ReplayCheckResult> => {
      const { GridCrashEngine, DIFFICULTIES } = await import("./engine.js");
      const difficulty = DIFFICULTIES[difficultyId];
      if (!difficulty) throw new Error(`unknown difficulty: ${difficultyId}`);

      const engine = new GridCrashEngine(difficulty, mode, seed);
      let applied = 0;
      for (const ev of events) {
        const state = engine.getState();
        if (state.phase === "WIN" || state.phase === "LOSE") break;
        if (ev.a === "r") engine.revealCell(ev.y, ev.x);
        else engine.toggleFlag(ev.y, ev.x);
        applied++;
      }
      const final = engine.getState();
      return { phase: final.phase, score: final.score, eventsApplied: applied };
    };

  // Opens the playback overlay on an arbitrary local stream — lets the
  // viewer be exercised without a live backend:
  //   __gridclashReplayWatch(events, "easy", "random", seed)
  (window as unknown as Record<string, unknown>)["__gridclashReplayWatch"] =
    async (
      events: ReplayEvent[],
      difficultyId: string,
      mode: "random" | "deterministic",
      seed: number
    ): Promise<void> => {
      const { openReplayView } = await import("../ui/replay_view.js");
      openReplayView({
        playerName: "DEV",
        finalScore: 0,
        difficultyId,
        mode,
        seed,
        events,
      });
    };
}
